import { useEffect, useState } from 'react';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { login, clearAuthError, selectAuthError, selectAuthStatus } from '../../features/auth/authSlice';
import '../../styles/login.css';

export default function LoginPage() {
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectAuthStatus);
  const serverError = useAppSelector(selectAuthError);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [localError, setLocalError] = useState('');

  const loading = status === 'loading';
  const error = localError || serverError;

  // a stale rejection shouldn't sit under the form once the user starts retyping
  useEffect(() => () => dispatch(clearAuthError()), [dispatch]);

  const submit = (e) => {
    e.preventDefault();
    setLocalError('');
    if (!email.trim()) return setLocalError('Please enter your email address.');
    if (!password) return setLocalError('Please enter your password.');
    dispatch(login({ email: email.trim(), password }));
  };

  const edit = (setter) => (e) => {
    setter(e.target.value);
    if (localError) setLocalError('');
    if (serverError) dispatch(clearAuthError());
  };

  const clear = () => {
    setEmail('');
    setPassword('');
    setLocalError('');
    dispatch(clearAuthError());
  };

  return (
    <div className="tr-login-wrap">
      <div className="tr-grid" />
      <span className="tr-particle p1" />
      <span className="tr-particle p2" />
      <span className="tr-particle p3" />
      <span className="tr-particle p4" />
      <span className="tr-particle p5" />
      <span className="tr-particle p6" />

      <div className="tr-login-container">
        {/* Left brand panel */}
        <div className="tr-login-left">
          <div className="tr-left-content">
            <div className="tr-company-logo">
              <img src={`${process.env.PUBLIC_URL}/brand-logo.png`} alt="TrustLab Diagnostics" />
            </div>
            <div className="tr-brand-sub">Diagnostics Private Limited</div>
            <a
              className="tr-app-title"
              href="https://trustrevenue.mytrustlab.co.in/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Trust <span>Swaeros Health Machine</span>
            </a>
            <p className="tr-app-desc">
              Revenue operations across every channel. Track collections, margins, leads and influencer payouts in one
              place.
            </p>
            <div className="tr-pulse-dots">
              <span /><span /><span /><span />
            </div>
          </div>
        </div>

        {/* Right sign-in form */}
        <div className="tr-login-right">
          <div className="tr-secure-badge">
            <GppGoodOutlinedIcon sx={{ fontSize: 12 }} /> SECURE
          </div>
          <div className="tr-form-title">
            Sign <span>In</span>
          </div>
          <div className="tr-form-subtitle">Welcome back. Enter your credentials to continue.</div>

          <form onSubmit={submit} noValidate>
            <div className="tr-input-group">
              <label className="tr-input-label" htmlFor="tr-email">Email</label>
              <div className="tr-input-wrap">
                <input
                  id="tr-email"
                  type="email"
                  value={email}
                  onChange={edit(setEmail)}
                  placeholder="Enter your email"
                  autoComplete="username"
                  disabled={loading}
                />
                <PersonOutlineIcon className="tr-input-icon" sx={{ fontSize: 18 }} />
              </div>
            </div>

            <div className="tr-input-group">
              <label className="tr-input-label" htmlFor="tr-password">Password</label>
              <div className="tr-input-wrap">
                <input
                  id="tr-password"
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={edit(setPassword)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  disabled={loading}
                />
                <LockOutlinedIcon className="tr-input-icon" sx={{ fontSize: 18 }} />
                <button
                  type="button"
                  className="tr-eye-toggle"
                  onClick={() => setShow((v) => !v)}
                  title={show ? 'Hide password' : 'Show password'}
                  aria-label={show ? 'Hide password' : 'Show password'}
                >
                  {show ? <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} /> : <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
                </button>
              </div>
              {error && <span className="tr-error" role="alert">{error}</span>}
            </div>

            <div className="tr-btn-row">
              <button type="submit" className={'tr-btn-signin' + (loading ? ' loading' : '')} disabled={loading}>
                {loading ? 'Signing In' : 'Sign In'}
              </button>
              <button type="button" className="tr-btn-clear" onClick={clear} disabled={loading}>
                Clear
              </button>
            </div>
          </form>

          <div className="tr-footer-text">
            <LockOutlinedIcon sx={{ fontSize: 11 }} /> 256-bit SSL encrypted · Authorized access only
          </div>
        </div>
      </div>
    </div>
  );
}
